import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Home, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function HeroSlider({ images, showBackButton = true, backLink = "/", backLabel = "Back to Home" }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <section className="overflow-hidden relative">
      <div className="overflow-hidden relative z-0 pt-14 w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <img
              src={images[currentSlide]}
              alt={`Hero Slide ${currentSlide + 1}`}
              className="w-full"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {images.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="flex absolute left-4 top-1/2 z-10 justify-center items-center w-10 h-10 text-white rounded-full backdrop-blur-sm transition-all bg-black/30 hover:bg-black/50 hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="flex absolute right-4 top-1/2 z-10 justify-center items-center w-10 h-10 text-white rounded-full backdrop-blur-sm transition-all bg-black/30 hover:bg-black/50 hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>

          <div className="flex absolute bottom-4 left-1/2 z-10 gap-2 -translate-x-1/2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentSlide === index
                    ? "bg-white w-8"
                    : "bg-white/50 hover:bg-white/75"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {showBackButton && (
        <motion.div
          className="absolute top-32 left-4 z-10"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Link
            to={backLink}
            className="flex gap-2 items-center px-4 py-2 text-white rounded-lg backdrop-blur-sm transition-all bg-black/30 hover:bg-black/50 hover:scale-105"
          >
            {backLink === "/" ? <Home size={18} /> : <ArrowLeft size={18} />}
            <span className="text-sm font-medium">{backLabel}</span>
          </Link>
        </motion.div>
      )}
    </section>
  );
}

